import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import './LoginButton.css';

function LoginButton({
  loggedInUser,
  onLogout,
  onLoginSuccess,
  externalOpen = false,
  externalMode = 'login',
  onExternalOpenChange,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (externalOpen) {
      setIsModalOpen(true);
      setMode(externalMode === 'signup' ? 'signup' : 'login');
      setError('');
    }
  }, [externalOpen, externalMode]);

  const resetFields = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError('');
    resetFields();
    setMode('login');
    onExternalOpenChange?.(false);
  };

  const openModal = (nextMode = 'login') => {
    setMode(nextMode);
    setIsModalOpen(true);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        closeModal();
        onLoginSuccess?.();
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      console.error('Error during login:', err);
      setError('Login failed. Please try again later.');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await apiFetch('/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          email: email.trim(),
          password,
          confirmPassword,
        }),
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        closeModal();
        onLoginSuccess?.();
      } else {
        setError(result.message || 'Signup failed');
      }
    } catch (err) {
      console.error('Error during signup:', err);
      setError('Signup failed. Please try again later.');
    }
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      onLogout?.();
      window.location.replace(`${window.location.origin}${window.location.pathname}#/`);
    }
  };

  return (
    <div className="login-container">
      {loggedInUser ? (
        <button type="button" className="login-button" onClick={handleLogout}>
          Logout
        </button>
      ) : (
        <div className="login-buttons">
          <button type="button" className="login-button" onClick={() => openModal('login')}>
            Login
          </button>
          <button type="button" className="login-button login-button--signup" onClick={() => openModal('signup')}>
            Sign Up
          </button>
        </div>
      )}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button type="button" className="close-button" onClick={closeModal}>X</button>
            <h2>{mode === 'signup' ? 'Sign Up' : 'Login'}</h2>
            {mode === 'signup' && (
              <p className="login-modal-hint">
                One email per account. Use a public address (.com, .gov, .net, .store, etc.) — not .local.
              </p>
            )}
            {error && <div className="error-message">{error}</div>}
            {mode === 'login' ? (
              <form onSubmit={handleLogin}>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button type="submit" className="submit-button">Log In</button>
              </form>
            ) : (
              <form onSubmit={handleSignup}>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  minLength={3}
                  maxLength={32}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button type="submit" className="submit-button">Create Account</button>
              </form>
            )}
            <p className="login-signup-link">
              {mode === 'login' ? (
                <>No account? <button type="button" className="link-button" onClick={() => { setMode('signup'); setError(''); }}>Sign up</button></>
              ) : (
                <>Already have an account? <button type="button" className="link-button" onClick={() => { setMode('login'); setError(''); }}>Log in</button></>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginButton;